import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Center,
  Circle,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useDropzone } from "react-dropzone";
import { useCreateTempFile } from "../../services/api/registries/mutations/useCreateTempFile";
import { LibFullWidthSpinner } from "../lib";
import { MaterialIcon } from "../shared/MaterialIcon";

interface IProps {
  title: string;
  fileHelperText?: string;
  isOpen: boolean;
  onClose: () => void;
  onFileChange: (fileName: string, tempFileKey?: string) => void;
  currentFileName?: string;
}

export const ManageFileModal = ({
  title,
  fileHelperText,
  onClose,
  isOpen,
  onFileChange,
  currentFileName,
}: IProps) => {
  const [uploadError, set_uploadError] = React.useState<string | null>(null);

  const toast = useToast();

  const {
    mutateAsync: mutateAsyncCreateTempFile,
    isLoading: isLoadingCreateTempFile,
    isError: isErrorCreateTempFile,
  } = useCreateTempFile();

  const onDrop = async (acceptedFiles) => {
    // Do something with the files
    set_uploadError(null);

    const file = acceptedFiles[0];

    if (!isValidFile(file)) {
      set_uploadError("File too big. Can you pick something smaller?");
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file.name);

    try {
      const tempFileLocation = await mutateAsyncCreateTempFile(formData);
      // setValue({ filename: file.name, fileLocation: location })
      toast({
        title: "File uploaded!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onFileChange(file.name, tempFileLocation);
    } catch (error) {
      toast({
        title: "Failed to upload",
        description: `We could not save this file.`,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    disabled: isLoadingCreateTempFile,
    accept: {
      "image/*": [],
      // "image/*": [".jpeg", ".png"],
      "application/pdf": [],
    },
  });

  const handleOnClose = () => {
    onClose();
  };

  const handleRemoveFile = () => {
    set_uploadError(null);
    onFileChange("");
  };

  const isValidFile = (file) => {
    const maxAllowedSize = 5 * 1024 * 1024;
    if (file.size > maxAllowedSize) {
      return false;
    } else {
      set_uploadError(null);
      return true;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      closeOnOverlayClick={true}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage File</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Center>
            <Stack spacing={8} maxW={"lg"} w="full">
              <Text textStyle={"header"} textAlign={"center"}>
                {title}
              </Text>
              <Center w="full">
                {/* Drop Zone */}
                <Stack
                  {...getRootProps()}
                  spacing="6"
                  borderColor="border"
                  borderStyle={"dashed"}
                  borderWidth="1px"
                  py={4}
                  px={6}
                  rounded="md"
                  w="full"
                  cursor={"pointer"}
                  _hover={{
                    bg: "bg.high",
                  }}
                >
                  <input {...getInputProps()} />

                  <Center>
                    {isLoadingCreateTempFile ? (
                      <Box>
                        <LibFullWidthSpinner size="lg" thickness="3px" />
                      </Box>
                    ) : (
                      <Circle borderWidth="4px" borderColor="text.softer" p={4}>
                        <MaterialIcon
                          color={"text.softer"}
                          name={"cloud_upload"}
                          variant="outline"
                          fontSize="5xl"
                        />
                      </Circle>
                    )}
                  </Center>

                  <VStack spacing={4} color="text.soft">
                    {isDragActive ? (
                      <Text>You got it! Now just drop it here...</Text>
                    ) : (
                      <Text>Drag and drop files here</Text>
                    )}

                    <HStack>
                      <Divider w="14"></Divider>
                      <Text>or</Text>
                      <Divider w="14"></Divider>
                    </HStack>

                    <Button isDisabled={isLoadingCreateTempFile}>
                      Select Files
                    </Button>
                    {fileHelperText && (
                      <Text fontSize="sm" mt="3">
                        {fileHelperText}
                      </Text>
                    )}
                  </VStack>
                </Stack>
              </Center>

              {currentFileName && (
                <HStack spacing={12} justify="space-between">
                  <Stack spacing={0} flex={1}>
                    <Text fontSize="sm" wordBreak={"break-all"}>
                      Existing file name
                    </Text>
                    <Text fontSize="xl">{currentFileName}</Text>
                  </Stack>

                  <Button
                    size="sm"
                    onClick={handleRemoveFile}
                    variant="outline"
                    colorScheme="red"
                  >
                    Delete existing file
                  </Button>
                </HStack>
              )}

              {uploadError && (
                <Alert status="error" variant={"solid"}>
                  <AlertIcon />
                  <Text>{uploadError}</Text>
                </Alert>
              )}
            </Stack>
          </Center>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button onClick={onClose}>Close</Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
